import clsx from "clsx";
import { type FC } from "react";

import { ParticipantCountIndicator } from "./ParticipantCountIndicator";
import { RecordingStatusIndicator } from "./RecordingStatusIndicator";

import { DisplayCaption } from "components/DisplayCaption";
import { GlobalBanners } from "components/GlobalBanners";
import {
  LOGO_HEADER_SIZE,
  MOBILE_OVERLAY_ROUTES,
  MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  MobileOverlayRouteType,
  TOPIC_ID_QUERY_PARAM,
} from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { BurgerMenuIcon, ChevronLeftIcon, CrossRemoveIcon, ForumLogoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  activitySelectors,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPage,
  selectCurrentSectionId,
  selectIsRecordingAgent,
  topicSelectors,
} from "store/selectors";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { useAv } from "utils/av";
import { useHomeUrl } from "utils/hooks/use-home-url";
import {
  useBorderedPresentationColoring,
  usePresentationCaption,
  useShouldShowPresentationCaption,
} from "utils/presentation";
import { Link, useQueryParamsManager } from "utils/routing";
import { pageUrl, sectionHomeUrl, useViewLevel } from "utils/urls";

/**
 * This is an alternative for the MainHeader component to be displayed on small screens.
 * It has a tiny subset of the tablet/desktop functionality, mainly removing all the
 * assessments, editing and instructor controls, breadcrumbs, and connection status,
 * but adding in mobile-related navigation functionality and menus.
 */
export const MobileMainHeader: FC = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const { setQueryParams, deleteQueryParams, getQueryParam } = useQueryParamsManager();
  const homeUrl = useHomeUrl();
  const selectedPage = useAppSelector(selectCurrentPage);
  const activity = useAppSelector((s) =>
    activitySelectors.selectById(s, selectedPage?.activity_id),
  );
  const topic = useAppSelector((s) => topicSelectors.selectById(s, activity?.topic_id));
  const selectedTopicId = getQueryParam(TOPIC_ID_QUERY_PARAM) || topic?.id;
  const isMobileMainMenuOpen =
    (getQueryParam(MOBILE_OVERLAY_ROUTE_QUERY_PARAM) as MobileOverlayRouteType) ===
    MOBILE_OVERLAY_ROUTES[0];

  const shouldShowPresentationCaption = useShouldShowPresentationCaption(true);
  const borderColorClass = useBorderedPresentationColoring();
  const { captionText, captionBgColor, linkText, linkUrl } = usePresentationCaption();

  const isLargeScreen = useIsLgOrLarger();
  const viewLevel = useViewLevel();

  const { isInVirtualClass } = useAv();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isInVirtualClassOrObserving = isInVirtualClass || isObservingClass;
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);

  const toggleMobileMainMenu = () => {
    if (isMobileMainMenuOpen) {
      deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM]);
    } else {
      setQueryParams({ [MOBILE_OVERLAY_ROUTE_QUERY_PARAM]: MOBILE_OVERLAY_ROUTES[0] });
    }
  };

  if (isLargeScreen) {
    console.warn("MobileMainHeader should not be used for large screens.");
  }

  function backTextAndUrl(): [string, string] {
    if (viewLevel.page && selectedTopicId && selectedPage && sectionId) {
      return [t("main_header.back"), pageUrl(sectionId, "", { topicId: selectedTopicId })];
    }

    return [t("main_header.back"), sectionHomeUrl(sectionId)];
  }

  const [backText, urlWithoutPage] = backTextAndUrl();

  return (
    <>
      <GlobalBanners />
      <header
        className={clsx(
          "group/themable box-content flex h-9 items-center justify-between bg-white px-4 py-2 text-black-tint-20 data-[dark]:border-black-tint-15 data-[dark]:bg-black-tint-15 data-[dark]:text-white",
          shouldShowPresentationCaption
            ? `border-b-2 ${borderColorClass}`
            : "border-b border-black-tint-90",
        )}
        data-dark={isInVirtualClassOrObserving || undefined}
      >
        <div className="flex w-full justify-between">
          {viewLevel.section || viewLevel.home ? (
            <div className="logo flex items-center gap-2">
              <Link
                aria-label="Forum"
                className="flex flex-shrink-0 cursor-pointer items-center border-0 text-black lg:border"
                target={isInVirtualClassOrObserving || isInInPersonClass ? "_blank" : undefined}
                to={homeUrl}
              >
                <ForumLogoIcon
                  color={isInVirtualClassOrObserving ? "white" : "black"}
                  width={LOGO_HEADER_SIZE}
                />
              </Link>
            </div>
          ) : (
            <div className="flex items-center justify-between truncate">
              <Link className="truncate" to={urlWithoutPage}>
                <IconText
                  iconStart={<ChevronLeftIcon />}
                  text={<span className="truncate">{backText}</span>}
                />
              </Link>
            </div>
          )}

          <div className="flex items-center gap-3">
            {isInVirtualClassOrObserving && <ParticipantCountIndicator />}
            {!isRecordingAgent && isInVirtualClassOrObserving && <RecordingStatusIndicator />}
            <SquareIconButton
              kind="ghost"
              size="s"
              title={
                isMobileMainMenuOpen ? t("common.close") : t("main_nav.toggle_mobile_main_menu")
              }
              onClick={toggleMobileMainMenu}
            >
              {isMobileMainMenuOpen ? <CrossRemoveIcon /> : <BurgerMenuIcon />}
            </SquareIconButton>
          </div>
        </div>
      </header>

      {shouldShowPresentationCaption && (
        <DisplayCaption
          bgColorClass={captionBgColor}
          caption={captionText}
          linkText={linkText}
          linkUrl={linkUrl}
        />
      )}
    </>
  );
};
